import {
  get,
  has,
  isObject,
  isString,
  map,
  memoize,
  parseInt,
  reduce,
  replace,
  toUpper,
} from "lodash";
import { Locale } from "@linkfluence/models/locale";

export interface Resources {
  [key: string]: Resources | string;
}

enum PluralSuffix {
  Zero = "zero",
  One = "one",
  Other = "other",
  None = "",
}

type KeyPrefix<T extends string> = T extends "" ? "" : `::${T}`;

export type KeyPath<T> = (
  T extends object
    ? {
        [K in Exclude<keyof T, symbol>]: `${K}${KeyPrefix<KeyPath<T[K]>>}`;
      }[Exclude<keyof T, symbol>]
    : ""
) extends infer D
  ? Extract<D, string>
  : never;

export interface ITranslationValues {
  [key: string]: string | number;
}

const pluralFetcher = {
  [Locale.German]: (count: number) => {
    if (!count) {
      return PluralSuffix.Zero;
    }
    if (count === 1) {
      return PluralSuffix.One;
    }
    return PluralSuffix.Other;
  },
  [Locale.English]: (count: number) => {
    if (count === 1) {
      return PluralSuffix.One;
    }
    return PluralSuffix.Other;
  },
  [Locale.French]: (count: number) => {
    if (!count) {
      return PluralSuffix.Zero;
    }
    if (count === 1) {
      return PluralSuffix.One;
    }
    return PluralSuffix.Other;
  },
  [Locale.Chinese]: (count: number) => {
    if (!count) {
      return PluralSuffix.Zero;
    }
    return PluralSuffix.Other;
  },
  [Locale.Japanese]: (count: number) => {
    if (!count) {
      return PluralSuffix.Zero;
    }
    return PluralSuffix.Other;
  },
};

const warnings = {};

const logUntranslatedKey = (key: string, locale: Locale): void => {
  if (process.env.NODE_ENV !== "test") {
    if (!warnings[locale]) {
      warnings[locale] = {};
    }
    if (!warnings[locale][key]) {
      console.warn(`Missing translation "${key}" for locale "${locale}"!`);
    }
    warnings[locale][key] = true;
  }
};

const memoizeUpper = memoize((key: string) => toUpper(key));

const memoizeColonized = memoize((key: string) => replace(key, /\./g, "::"));

const memoizeDotted = memoize((key: string) => replace(key, /::/g, "."));

const getMessage = (
  resources: Resources,
  cleanKey: string,
  locale: Locale,
  pluralSuffix: PluralSuffix
): string => {
  //@ts-ignore
  if (locale === "dev" || locale === "tl_PH") {
    // tl_PH is used as a placeholder for the dev language (show raw key)
    return cleanKey;
  }
  const colonizedKey = memoizeColonized(cleanKey);
  const dottedKey = memoizeDotted(cleanKey);
  const path =
    pluralSuffix !== PluralSuffix.None &&
    has(resources, locale + "." + dottedKey + "." + pluralSuffix)
      ? dottedKey + "." + pluralSuffix
      : dottedKey;
  const translation: string = get(resources[locale], path, colonizedKey);
  if (translation === colonizedKey || translation === "") {
    logUntranslatedKey(colonizedKey, locale);
    return locale !== Locale.English
      ? getMessage(resources, cleanKey, Locale.English, pluralSuffix)
      : colonizedKey;
  } else {
    //prevent crash if the requested key is a parent
    return isObject(translation) ? colonizedKey : translation;
  }
};

const getSuffix = (
  values: ITranslationValues,
  field: string,
  locale: Locale
) => {
  if (has(values, field)) {
    const _values = values[field];
    const localizedPluralFetcher =
      pluralFetcher[locale] ?? pluralFetcher[Locale.English];
    return localizedPluralFetcher(
      isString(_values) ? parseInt(_values) : _values
    );
  } else {
    return PluralSuffix.None;
  }
};

/**
 * Translate string given resources, key, locale…
 * @param {Object} resources JSON structured translation strings
 * @param {string} [locale=en_GB] Current locale string
 * @param {string} key Path of string in resources JSON
 * @param {Object} [values] Pairs of {variable: value} to replace in the string
 * @param {string} [field] Variable taken for pluralization (defaults to 'count')
 * @returns {string} Translated string or KEY if not found
 */
export function tr<T>(
  resources: Resources,
  locale: Locale = Locale.English,
  key: T,
  values: ITranslationValues = {},
  field: string = "count"
) {
  if (!resources) {
    throw new Error("Missing `resources` parameter.");
  }
  if (!key) {
    throw new Error("Missing `key` parameter.");
  }
  const cleanKey = memoizeUpper(key as unknown as string);
  const pluralSuffix = getSuffix(values, field, locale);
  return reduce(
    values,
    (acc, value, cleanKey: string) => {
      return replace(
        replace(
          replace(acc, "{ " + cleanKey + " }", "" + value),
          "{" + cleanKey + "}",
          "" + value
        ),
        new RegExp("__" + cleanKey + "__", "g"),
        "" + value
      );
    },
    getMessage(resources, cleanKey, locale, pluralSuffix)
  );
}

export function fpTr(resources: Resources) {
  return (
    locale: Locale = Locale.English,
    key: string,
    values: ITranslationValues = {},
    field: string = "count"
  ) => {
    return tr(resources, locale, key, values, field);
  };
}

export function fpTranslateEntry(resources: Resources) {
  return <T extends object>(
    locale: Locale,
    entry: T,
    fields: string[] = ["label"]
  ): T => {
    return {
      ...entry,
      ...reduce(
        fields,
        (acc, field) => {
          if (entry[field]) {
            return {
              ...acc,
              [field]: fpTr(resources)(locale, entry[field]),
            };
          } else {
            return acc;
          }
        },
        {}
      ),
    };
  };
}

export function fpTranslateEntries(resources: Resources) {
  return <T extends object>(
    locale: Locale,
    entries: T[],
    fields: string[] = ["label"]
  ): T[] => {
    return map(entries, (entry) =>
      fpTranslateEntry(resources)(locale, entry, fields)
    );
  };
}

export default tr;
